'use client';
import { MapPin, ChevronDown, ChevronUp, ArrowRight, Phone } from 'lucide-react';
import { useMemo } from 'react';
import { useCmsData } from '@/lib/CmsContext';
import { AppLink } from '@/lib/appLink';
import {
  mapGeoAreaToRegions,
  normalizeBlockRegions,
  REGION_SERVICE_COLORS,
} from '@/lib/regionsSectionUtils';

const DEFAULT_STATS = [
  { value: '3', label: 'Області' },
  { value: '15+', label: 'Районів' },
  { value: '100+', label: 'Населених пунктів' },
  { value: '0 грн', label: 'Виїзд на оцінку' },
];

/**
 * Regions accordion — native <details> so open/close works without React hydration (prod proxy).
 *
 * @param {{ section?: import('@/types/cms').RegionsSectionPageSection }} props
 */
export default function RegionsSection({ section = {} }) {
  const cms = useCmsData();

  const subTitle = section.sub_title || 'Географія';
  const title = section.title || 'Регіони будівництва та ремонту';
  const intro =
    section.intro ||
    'Будівництво, фасадні роботи, ремонт та реконструкція у Волинській і Рівненській областях та суміжних районах Львівської області. Працюємо в Луцьку, Ковелі, Рівному та околицях. Безкоштовний виїзд на оцінку обсягу робіт.';
  const showStats = section.show_stats !== false;
  const stats = section.stats?.length ? section.stats : DEFAULT_STATS;

  const ctaTitle = section.cta_title || 'Не знайшли свій населений пункт?';
  const ctaText =
    section.cta_text ||
    "Зателефонуйте нам — обговоримо умови виїзду індивідуально. Ми часто беремо об'єкти за межами основного регіону.";
  const ctaButtonLabel = section.cta_button_label || "Зв'язатися";
  const ctaButtonHref = section.cta_button_href || '/contacts';

  const regions = useMemo(() => {
    const useGeo = section.use_geo_catalog !== false;
    const customRegions = normalizeBlockRegions(section.regions || []);

    if (!useGeo && customRegions.length) {
      return customRegions;
    }

    const fromGeo = mapGeoAreaToRegions(cms?.geo_area || []);
    if (fromGeo.length) {
      return fromGeo;
    }

    return customRegions;
  }, [cms?.geo_area, section.regions, section.use_geo_catalog]);

  return (
    <section className="bg-card border-t border-border py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">{subTitle}</span>
            <div className="w-8 h-px bg-gold" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-4">{title}</h2>
          <div className="gold-divider" />
          {intro ? (
            <p className="text-muted-foreground mt-6 max-w-2xl mx-auto text-base leading-relaxed">{intro}</p>
          ) : null}
        </div>

        {showStats && stats.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
            {stats.map((stat) => (
              <div key={`${stat.value}-${stat.label}`} className="bg-background rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-black text-gold">{stat.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="space-y-4" data-grv-regions>
          {regions.map((region) => {
            const districtLabel =
              region.districts.length <= 4 ? 'райони' : 'районів';

            return (
              <details
                key={region.name}
                data-grv-region-item
                className="grv-region-item rounded-2xl border border-border bg-background/50 overflow-hidden transition-all duration-300 open:border-gold/50 open:bg-background open:shadow-lg open:shadow-gold/5"
              >
                <summary className="grv-region-summary w-full flex items-center justify-between px-6 py-5 text-left group cursor-pointer list-none">
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="w-4 h-4 rounded-full border-2 shrink-0"
                      style={{ borderColor: region.color, background: `${region.color}33` }}
                    />
                    <div className="min-w-0">
                      <span className="grv-region-title font-black text-lg text-foreground group-hover:text-gold transition-colors">
                        {region.name}
                      </span>
                      {region.desc ? (
                        <p className="grv-region-preview text-xs text-muted-foreground mt-0.5 max-w-lg hidden sm:block">
                          {region.desc.substring(0, 90)}…
                        </p>
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full font-semibold shrink-0">
                      {region.districts.length} {districtLabel}
                    </span>
                  </div>
                  <ChevronDown className="grv-region-chevron-down w-5 h-5 text-muted-foreground shrink-0 group-hover:text-gold transition-colors" />
                  <ChevronUp className="grv-region-chevron-up w-5 h-5 text-gold shrink-0" />
                </summary>

                <div className="border-t border-border/50">
                  {region.desc ? (
                    <div className="px-6 py-4 bg-gold/5 border-b border-gold/10">
                      <p className="text-sm text-foreground/80 leading-relaxed">{region.desc}</p>
                    </div>
                  ) : null}

                  <div className="p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {region.districts.map((district) => (
                      <AppLink
                        key={district.slug}
                        href={`/region-${district.slug}`}
                        className="group/card block rounded-xl border border-border bg-card hover:border-gold/50 hover:shadow-md hover:shadow-gold/5 transition-all duration-200 p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" />
                            <span className="font-bold text-sm text-foreground group-hover/card:text-gold transition-colors leading-tight">
                              {district.name}
                            </span>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover/card:text-gold group-hover/card:translate-x-0.5 transition-all shrink-0 mt-0.5" />
                        </div>

                        {district.desc ? (
                          <p className="text-xs text-muted-foreground leading-relaxed mb-3">{district.desc}</p>
                        ) : null}

                        {district.services?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {district.services.map((service) => (
                              <span
                                key={service}
                                className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                                  REGION_SERVICE_COLORS[service] || 'bg-muted text-muted-foreground border-border'
                                }`}
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </AppLink>
                    ))}
                  </div>
                </div>
              </details>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-gold/20 bg-gold/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gold shrink-0 mt-1" />
            <div>
              <p className="text-foreground font-bold mb-1">{ctaTitle}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{ctaText}</p>
            </div>
          </div>
          <AppLink
            href={ctaButtonHref}
            className="shrink-0 flex items-center gap-2 bg-gold-gradient text-dark font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-sm uppercase tracking-wider"
          >
            <Phone className="w-4 h-4" />
            {ctaButtonLabel}
          </AppLink>
        </div>
      </div>
    </section>
  );
}
